import React from 'react';
import styled, { css } from 'styled-components';
import { AssetThumb, AssetThumbKind } from './AssetThumb';

export type GenerationEntry = {
  id: string;
  kind: AssetThumbKind;
  src?: string;
  posterSrc?: string;
  label?: string;
  pinned?: boolean;
};

export type GenerationSession = {
  id: string;
  title: string;
  entries: GenerationEntry[];
};

export type GenerationsRailProps = {
  title?: string;
  sessions: GenerationSession[];
  activeEntryId?: string;
  emptyHint?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onEntryClick?: (entry: GenerationEntry, session: GenerationSession) => void;
  onTogglePin?: (entry: GenerationEntry, session: GenerationSession) => void;
  /** Optional content rendered above the rail title (e.g. media-type tabs). */
  headerSlot?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
};

const Root = styled.aside`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  height: 100%;
  min-height: 0;
  background: var(--color-surface-subtle);
  border-right: 1px solid var(--color-border-subtle);
  padding: var(--space-4) var(--space-3);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const Title = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SearchField = styled.input`
  appearance: none;
  width: 100%;
  height: 32px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);

  &::placeholder {
    color: var(--color-text-tertiary);
  }
  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const Scroll = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border-strong) transparent;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding-right: var(--space-1);
`;

const SessionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-left: var(--space-2);
  border-left: 2px solid var(--color-border-subtle);
`;

const SessionHeader = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  background: ${(p) => (p.$active ? 'var(--color-accent-subtle)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--color-accent-text)' : 'var(--color-text-secondary)')};
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-align: left;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 4px var(--space-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
  width: 100%;
  transition: background var(--duration-fast) var(--ease-standard);

  &:hover {
    background: ${(p) =>
      p.$active ? 'var(--color-accent-subtle)' : 'var(--color-surface-inset)'};
    color: var(--color-text);
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  font-size: 9px;
  transition: transform var(--duration-fast) var(--ease-standard);
  transform: rotate(${(p) => (p.$open ? '90deg' : '0deg')});
  color: var(--color-text-tertiary);
`;

const SessionCount = styled.span`
  margin-left: auto;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
`;

const SessionEmpty = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  padding: var(--space-2) var(--space-1);
  font-style: italic;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
`;

const EntryWrap = styled.div<{ $active: boolean }>`
  position: relative;
  border-radius: var(--radius-lg);
  ${(p) =>
    p.$active &&
    css`
      outline: 2px solid var(--color-accent);
      outline-offset: 1px;
    `}
`;

const PinButton = styled.button<{ $pinned: boolean }>`
  position: absolute;
  top: 6px;
  left: 6px;
  appearance: none;
  border: none;
  background: ${(p) => (p.$pinned ? 'var(--color-accent)' : 'rgba(0, 0, 0, 0.55)')};
  color: #fff;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-pill);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  opacity: ${(p) => (p.$pinned ? 1 : 0)};
  transition: opacity var(--duration-fast) var(--ease-standard);

  ${EntryWrap}:hover & {
    opacity: 1;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--space-6) var(--space-3);
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  line-height: var(--line-height-base);
`;

const Footer = styled.div`
  border-top: 1px solid var(--color-border-subtle);
  padding-top: var(--space-3);
`;

export const GenerationsRail: React.FC<GenerationsRailProps> = ({
  title = 'Generations',
  sessions,
  activeEntryId,
  emptyHint = 'Past generations will appear here.',
  searchValue,
  onSearchChange,
  onEntryClick,
  onTogglePin,
  headerSlot,
  footer,
  className,
}) => {
  const isEmpty = sessions.every((s) => s.entries.length === 0);

  const activeSessionId = React.useMemo(() => {
    if (!activeEntryId) return null;
    return (
      sessions.find((s) => s.entries.some((e) => e.id === activeEntryId))?.id ?? null
    );
  }, [sessions, activeEntryId]);

  const [expandedSessionIds, setExpandedSessionIds] = React.useState<Set<string>>(
    () => new Set(activeSessionId ? [activeSessionId] : [])
  );

  React.useEffect(() => {
    if (activeSessionId) {
      setExpandedSessionIds((prev) => {
        if (prev.has(activeSessionId)) return prev;
        const next = new Set(prev);
        next.add(activeSessionId);
        return next;
      });
    }
  }, [activeSessionId]);

  const toggleSession = (id: string) =>
    setExpandedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Root className={className}>
      <Header>
        {headerSlot}
        <Title>{title}</Title>
        {onSearchChange && (
          <SearchField
            type="search"
            placeholder="Search history"
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        )}
      </Header>

      <Scroll>
        {isEmpty ? (
          <EmptyState>{emptyHint}</EmptyState>
        ) : (
          sessions.map((session) => {
            const open = expandedSessionIds.has(session.id);
            const isActiveSession = session.id === activeSessionId;
            return (
              <SessionBlock key={session.id}>
                <SessionHeader
                  type="button"
                  $active={isActiveSession}
                  aria-expanded={open}
                  onClick={() => toggleSession(session.id)}
                >
                  <Caret $open={open}>▶</Caret>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {session.title}
                  </span>
                  <SessionCount>{session.entries.length}</SessionCount>
                </SessionHeader>
                {open && (
                  session.entries.length === 0 ? (
                    <SessionEmpty>No steps yet</SessionEmpty>
                  ) : (
                    <Grid>
                      {session.entries.map((entry) => (
                        <EntryWrap key={entry.id} $active={entry.id === activeEntryId}>
                          <AssetThumb
                            kind={entry.kind}
                            src={entry.src}
                            posterSrc={entry.posterSrc}
                            aspect="9:16"
                            onClick={() => onEntryClick?.(entry, session)}
                            footer={entry.label}
                          />
                          {onTogglePin && (
                            <PinButton
                              type="button"
                              aria-label={entry.pinned ? 'Unpin' : 'Pin'}
                              $pinned={Boolean(entry.pinned)}
                              onClick={(event) => {
                                event.stopPropagation();
                                onTogglePin(entry, session);
                              }}
                            >
                              ★
                            </PinButton>
                          )}
                        </EntryWrap>
                      ))}
                    </Grid>
                  )
                )}
              </SessionBlock>
            );
          })
        )}
      </Scroll>

      {footer && <Footer>{footer}</Footer>}
    </Root>
  );
};

GenerationsRail.displayName = 'GenerationsRail';
